/**
 * BETA — caisse (cash register session) lifecycle.
 */
import { httpError } from "../router.js";
import { paginate, newId, nowIso, money } from "./_utils.js";
import { getDemoUser } from "../data/users.js";

export function register(route) {
  route("GET", "/caisse", (ctx) => {
    const { params, store } = ctx;
    let rows = [...store.data.caisses];
    if (params.statut) rows = rows.filter((c) => c.statut === params.statut);
    if (params.caissier_id) rows = rows.filter((c) => c.caissier_id === params.caissier_id);
    if (params.date_debut) rows = rows.filter((c) => c.date_ouverture >= params.date_debut);
    if (params.date_fin) rows = rows.filter((c) => c.date_ouverture <= params.date_fin + "T23:59:59.999Z");
    rows.sort((a, b) => (a.date_ouverture > b.date_ouverture ? -1 : 1));
    return paginate(rows, params);
  });

  route("GET", "/caisse/current", (ctx) => {
    const me = getDemoUser(ctx.store.role || "PROPRIETAIRE");
    const c = ctx.store.data.caisses.find(
      (x) => x.caissier_id === me.id && x.statut === "OUVERTE",
    );
    if (!c) return null;
    return enrichCaisse(c, ctx.store);
  });

  route("GET", "/caisse/:id", (ctx) => {
    const c = ctx.store.data.caisses.find((x) => x.id === ctx.params.id);
    if (!c) httpError(404, "Caisse introuvable", "NOT_FOUND");
    return enrichCaisse(c, ctx.store);
  });

  // Trésorerie TWP-16 (W5) — the demo officine has no comptoir account: the opening proposal is
  // « sans comptoir », pre-filled with the officine's fonds de caisse (TD-24).
  route("GET", "/caisse/ouverture", () => ({
    mode: "SANS_COMPTOIR",
    etat: "SANS_COMPTOIR",
    origine_choix: null,
    peut_choisir: false,
    comptoirs: [],
    fonds_caisse_defaut: "10000.00",
    comptoir: null,
    attendu: null,
    premiere_ouverture: false,
    refus: null,
  }));

  route("POST", "/caisse/open", (ctx) => {
    const me = getDemoUser(ctx.store.role || "PROPRIETAIRE");
    const targetId = ctx.body?.caissier_id || me.id;
    const existing = ctx.store.data.caisses.find(
      (x) => x.caissier_id === targetId && x.statut === "OUVERTE",
    );
    if (existing) httpError(409, "Caisse déjà ouverte pour cet utilisateur", "CAISSE_DEJA_OUVERTE");
    const target = ctx.store.data.users.find((u) => u.id === targetId) || me;
    const id = newId("caisse-new");
    const created = {
      id,
      numero: `CAISSE-2026-${id.slice(-5).toUpperCase()}`,
      caissier_id: target.id,
      caissier: { id: target.id, nom: target.nom, prenom: target.prenom },
      statut: "OUVERTE",
      date_ouverture: nowIso(),
      date_cloture: null,
      fonds_ouverture: Number(ctx.body?.fonds_ouverture) || 0,
      fonds_cloture: 0,
      ca_libre: 0,
      ca_chifa: 0,
      ca_total: 0,
      nb_ventes: 0,
      ecart: 0,
      notes: "",
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    ctx.store.data.caisses.unshift(created);
    ctx.commit();
    return created;
  });

  route("POST", "/caisse/:id/close", (ctx) => {
    const c = ctx.store.data.caisses.find((x) => x.id === ctx.params.id);
    if (!c) httpError(404, "Caisse introuvable", "NOT_FOUND");
    if (c.statut !== "OUVERTE") httpError(400, "Caisse déjà clôturée", "NOT_OPEN");
    const fondsClot = Number(ctx.body?.fonds_cloture) || 0;
    const enriched = enrichCaisse(c, ctx.store);
    c.statut = "CLOTUREE";
    c.date_cloture = nowIso();
    c.fonds_cloture = fondsClot;
    c.ca_libre = enriched.ca_libre;
    c.ca_chifa = enriched.ca_chifa;
    c.ca_total = enriched.ca_total;
    c.nb_ventes = enriched.nb_ventes;
    c.ecart = money(fondsClot - (c.fonds_ouverture + c.ca_libre + c.ca_chifa));
    c.notes = ctx.body?.notes || c.notes;
    c.updated_at = nowIso();
    ctx.commit();
    return c;
  });
}

function enrichCaisse(c, store) {
  const ventes = store.data.ventes.filter(
    (v) => v.caisse_id === c.id && v.statut === "VALIDEE",
  );
  const ca_libre = money(
    ventes.filter((v) => v.type_vente === "LIBRE").reduce((s, v) => s + (v.montant_total_ttc || 0), 0),
  );
  const ca_chifa = money(
    ventes
      .filter((v) => v.type_vente === "CHIFA")
      .reduce((s, v) => s + (v.montant_a_payer || 0), 0),
  );
  return { ...c, ca_libre, ca_chifa, ca_total: money(ca_libre + ca_chifa), nb_ventes: ventes.length };
}
